import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getConfig } from '../../../api/auth';
import { updateConfig } from '../../../api/administration';
import SettingsHeader from './components/SettingsHeader';
import SettingsTable from './components/SettingsTable';
import SettingsModal from './components/SettingsModal';
import AddConfigModal from './components/AddConfigModal';

const Settings = () => {
  // Load initial data from localStorage (set during login)
  const getStoredConfig = () => {
    try {
      const stored = localStorage.getItem('reference');
      if (!stored) return { configValue: {}, configId: null };

      const data = JSON.parse(stored);

      // Check if stored data has the new structure with id and value
      if (data && data.id && data.value) {
        return { configValue: data.value, configId: data.id };
      }

      // Fallback: treat as config value directly (old format)
      return { configValue: data, configId: null };
    } catch (e) {
      console.warn('Could not parse stored config');
      return { configValue: {}, configId: null };
    }
  };

  const storedData = getStoredConfig();
  const [configData, setConfigData] = useState(storedData.configValue);
  const [configRecordId, setConfigRecordId] = useState(storedData.configId);
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [accessDenied, setAccessDenied] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfigKey, setSelectedConfigKey] = useState(null);
  const [selectedConfigData, setSelectedConfigData] = useState(null);

  // Add Config modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Fetch fresh config data from API
  const getData = async () => {
    setLoading(true);
    try {
      const response = await getConfig();
      console.log('Config data:', response);

      if (response && response.data) {
        const record = response.data.data || response.data || {};
        console.log('Config record:', record);

        // New API structure: { id, client, company, division, type, value, ... }
        // Extract the config record ID and the value object
        const recordId = record.id;
        const configValue = record.value || {};

        console.log('Config record ID:', recordId);
        console.log('Config value:', configValue);

        // Store the record ID for updates
        if (recordId) {
          setConfigRecordId(recordId);
        }

        // Set the config data (the value object contains the actual settings)
        setConfigData(configValue);

        // Update localStorage with the full record structure
        localStorage.setItem('reference', JSON.stringify(record));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (statusCode === 401 || errorMessage.toLowerCase().includes('unauthorized')) {
        // If API fails, try to use localStorage data
        const stored = getStoredConfig();
        if (Object.keys(stored.configValue).length > 0) {
          toast.warning('Using cached configuration data');
          return;
        }
        setAccessDenied(true);
      } else if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        setAccessDenied(true);
      } else if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error(errorMessage || 'Failed to load configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  // Always fetch fresh config on mount to ensure configRecordId is available
  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search handler
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Page size handler
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Open modal for editing
  const handleEditType = (key) => {
    setSelectedConfigKey(key);
    setSelectedConfigData(configData[key]);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedConfigKey(null);
    setSelectedConfigData(null);
  };

  // Add Config modal handlers
  const openAddModal = () => {
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
  };

  // Delete a config type
  const handleDeleteType = async (key) => {
    if (!window.confirm(`Are you sure you want to delete "${formatTypeName(key)}"? This action cannot be undone.`)) {
      return;
    }

    if (!configRecordId) {
      toast.error('Configuration ID not found. Please refresh and try again.');
      return;
    }

    setSaving(true);
    try {
      const updatedConfigValue = { ...configData };
      delete updatedConfigValue[key];

      const payload = { value: updatedConfigValue };
      await updateConfig(configRecordId, payload);

      setConfigData(updatedConfigValue);

      const storedRecord = JSON.parse(localStorage.getItem('reference') || '{}');
      storedRecord.value = updatedConfigValue;
      localStorage.setItem('reference', JSON.stringify(storedRecord));

      toast.success(`${formatTypeName(key)} deleted successfully`);
    } catch (error) {
      console.error('Error deleting config:', error);
      const errorMessage = error.response?.data?.message || error.message || '';
      toast.error(errorMessage || 'Failed to delete configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save new config from add modal
  const handleAddConfigSave = async (key, configValue) => {
    setSaving(true);
    try {
      if (!configRecordId) {
        toast.error('Configuration ID not found. Please refresh and try again.');
        setSaving(false);
        return;
      }

      const updatedConfigValue = {
        ...configData,
        [key]: configValue,
      };

      const payload = { value: updatedConfigValue };
      await updateConfig(configRecordId, payload);

      setConfigData(updatedConfigValue);

      const storedRecord = JSON.parse(localStorage.getItem('reference') || '{}');
      storedRecord.value = updatedConfigValue;
      localStorage.setItem('reference', JSON.stringify(storedRecord));

      toast.success(`${formatTypeName(key)} added successfully`);
      closeAddModal();
    } catch (error) {
      console.error('Error adding config:', error);
      const errorMessage = error.response?.data?.message || error.message || '';
      toast.error(errorMessage || 'Failed to add configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save changes from modal - updates the specific config type via API
  const handleModalSave = async (key, updatedData) => {
    setSaving(true);
    try {
      // Use the stored config record ID
      console.log('Config record ID:', configRecordId);
      console.log('Updating key:', key, 'with data:', updatedData);

      if (!configRecordId) {
        console.error('Config record ID not found');
        toast.error('Configuration ID not found. Please refresh and try again.');
        setSaving(false);
        return;
      }

      // Build the updated value object with all config types
      const updatedConfigValue = {
        ...configData,
        [key]: updatedData,
      };

      // Build the payload for PUT /configs/{id}
      // Send the complete value object containing all config types
      const payload = {
        value: updatedConfigValue,
      };

      console.log('Sending payload:', payload);

      // Call the update API with config record ID and payload
      await updateConfig(configRecordId, payload);

      // Update local state
      setConfigData(updatedConfigValue);

      // Update localStorage with full record structure
      const storedRecord = JSON.parse(localStorage.getItem('reference') || '{}');
      storedRecord.value = updatedConfigValue;
      localStorage.setItem('reference', JSON.stringify(storedRecord));

      toast.success(`${formatTypeName(key)} saved successfully`);
      closeModal();
    } catch (error) {
      console.error('Error saving config:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 400) {
        toast.error(errorMessage || 'Invalid request. Please check your data');
      } else if (statusCode === 403) {
        toast.error('Access denied: You do not have permission to update configuration');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 404) {
        toast.error('Configuration record not found');
      } else {
        toast.error(errorMessage || 'Failed to save configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  // Format type name for display
  const formatTypeName = (key) => {
    if (!key) return '';
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Filter config data based on search term
  const getFilteredConfigData = () => {
    if (!searchTerm) return configData;

    const searchLower = searchTerm.toLowerCase();
    const filtered = {};

    Object.entries(configData).forEach(([key, value]) => {
      const keyMatches = key.toLowerCase().includes(searchLower);
      const descriptionMatches = value?.description?.toLowerCase().includes(searchLower);

      if (keyMatches || descriptionMatches) {
        filtered[key] = value;
      }
    });

    return filtered;
  };

  // Show access denied screen
  if (accessDenied) {
    return (
      <div className="px-5">
        <div className="border border-gray-300 mt-5 rounded-lg">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
            <p className="text-gray-500 text-center max-w-md">
              You are not authorized to manage settings. Please contact your Admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5">
      <div className="border border-gray-300 mt-5 rounded-lg relative">
        {/* Header */}
        <SettingsHeader
          onReload={getData}
          onAddConfig={openAddModal}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <SettingsTable
            data={getFilteredConfigData()}
            onEditType={handleEditType}
            onDeleteType={handleDeleteType}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleModalSave}
        configKey={selectedConfigKey}
        configData={selectedConfigData}
        isSubmitting={isSaving}
      />

      {/* Add Configuration Modal */}
      <AddConfigModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSave={handleAddConfigSave}
        existingKeys={Object.keys(configData)}
        isSubmitting={isSaving}
      />
    </div>
  );
};

export default Settings;
